// Single source of truth for the widths and named variants image delivery is
// allowed to request (ADO 61696 §2.5). Templates draw from this set rather
// than inventing widths, which keeps the number of cached derived images
// bounded — under Cloudflare every distinct width is a separately cached
// derivative, so an unbounded set is an unbounded cache.
//
// Everything here is live: the variants back the named accessors in
// app/utils/format/images.ts, the srcset lists back `getSrcSet`, and the
// missing-image fallback rounds up through the same widths. Editing one
// changes delivery.
//
// Kept in step with utils/format/imageSizes.ts in StampinUp.Ordering.Frontend
// so both repos emit the same widths during the Nuxt 4 migration. The `sizes`
// ladders are the exception: they describe this repo's rendered layout and are
// measured here (ADO 63438), not copied.

import type { ImageTransform } from '~/utils/format/imageUrl'
import { breakpoints, minWidthQuery } from '~/config/breakpoints'

// ─── Approved widths ─────────────────────────────────────────────────────
// The union of the widths the seam emits today and the widths the 61696
// variant table introduces. Ordered smallest to largest.
export const APPROVED_WIDTHS = [200, 240, 300, 360, 512, 600, 768, 800, 1024, 1200, 2000, 2100] as const

export type ApprovedWidth = typeof APPROVED_WIDTHS[number]

export const isApprovedWidth = (width: number): width is ApprovedWidth =>
  (APPROVED_WIDTHS as readonly number[]).includes(width)

// ─── Named variants ──────────────────────────────────────────────────────
// The variant table from the UI/UX handoff on ADO 61696 (§1.2), extended in
// ADO 62980. Sizes are stated as descriptors, not URLs, so the image serializer
// owns the URL shape.
//
// Quality and format are deliberately absent: the accessors in
// app/utils/format/images.ts merge those in, which keeps the delivery-quality
// baseline (ADO 63003) resolved by the serializer rather than pinned here.
//
// HiDPI is baked into the width rather than sent as a `dpr` param (ADO 62980).
// The descriptor carries `dpr` and the serializer emits it, but a `dpr`
// multiplier would put the effective pixel width outside `APPROVED_WIDTHS`
// and out of reach of `isApprovedWidth` — the governed set would stop
// describing what delivery actually requests. Every width below is already
// the 2x figure for its slot; see `VARIANT_RENDER_WIDTHS`.
//
// `banner-vertical` is constrained by height rather than width — the vertical
// hero master tops out at 2184px tall.
export type ImageVariant =
  | 'thumbnail'
  | 'card'
  | 'pdp-main'
  | 'pdp-zoom'
  | 'banner'
  | 'banner-vertical'

export const BANNER_VERTICAL_HEIGHT = 2184

// Every variant is `scale-down`, which never upscales a master past its stored
// size (61696 §2.2) and never crops. The handoff specified `thumbnail` as a
// 200x200 `cover` crop; every thumb slot in this repo sizes with `height: auto`
// and preserves aspect, so a square crop would visibly re-frame cart, order,
// wishlist and search rows rather than just resize them (ADO 62980).
export const IMAGE_VARIANTS: Record<ImageVariant, ImageTransform> = {
  // Cart rows, order history, subscriptions, back-in-stock, search results, the
  // wishlist summary grid and the desktop PDP thumbnail strip.
  //
  // 240 rather than the handoff's 200: the widest slot this variant fills is the
  // PDP strip at a measured 119 CSS, so 2x is 238 and 200 lands 17% short there.
  // The 61696 figure was drawn for the ~100px cluster the other eight call sites
  // sit in. 300 is the next approved width above 240 and would break SC-008's
  // upper bound (300 > 120 x 2), so 240 is the only width that satisfies both
  // ends. It costs the small slots 20% more bytes than 200 did; a second tier
  // for two placements would buy a separately cached derivative for a 20%
  // resolution delta, which the bounded-cache rule above does not justify.
  thumbnail: { width: 240, fit: 'scale-down' },
  // Category, listing and featured grid tiles, and marketing-toolkit assets.
  card: { width: 600, fit: 'scale-down' },
  // PDP primary image. Also the source the hover zoom magnifies: `useImageZoom`
  // applies `scale(2)` to the rendered element, so 2x the CSS slot serves both
  // a HiDPI screen and the zoom, but not both at once — the lightbox is what
  // covers that, at `pdp-zoom`.
  'pdp-main': { width: 1200, fit: 'scale-down' },
  // PDP zoom and lightbox.
  'pdp-zoom': { width: 2000, fit: 'scale-down' },
  // Full-width marketing banners.
  banner: { width: 2100, fit: 'scale-down' },
  // Vertical banner / hero placement.
  'banner-vertical': { height: BANNER_VERTICAL_HEIGHT, fit: 'scale-down' }
}

// ─── Render widths (SC-008) ──────────────────────────────────────────────
// The widest CSS pixel size each variant is rendered at, measured from the
// stylesheets that lay these placements out. SC-008 — no placement requests
// more pixels than it renders — is the assertion that no variant width exceeds
// twice the figure below, and the spec enforces it.
//
// These are documentation with teeth: change a placement's CSS and the number
// here has to move with it, or the test fails. Sources:
//
// Figures marked (measured) were read off the running app with
// getBoundingClientRect at viewports from 414 to 1920; the rest are from the
// stylesheet. Measuring matters: `.img` in ProductCard carries
// `max-width: 480px`, but the grid cell never gets near it, so 480 would have
// licensed a candidate 2.4x the real slot.
//
//  thumbnail       (measured 119) desktop PDP strip via ProductImageList;
//                  (measured 99) WishlistSummaryCard's 2x2 grid, whose card is
//                  capped at 240px so its cells cannot exceed ~116;
//                  LineItemImage 64-95, NotificationTable 100,
//                  SearchProducts 65, OrderDetailsRow 95, PDP small thumb 70
//  card            (measured 309) homepage featured tile at 1920; the category
//                  grid tops out at 265 and the SparkIt tile is ~290
//  pdp-main        (measured 593) ProductMediaContainer at 1920, where the
//                  container caps; 552 at 1440, 352 at 960
//  pdp-zoom        ProductOverlay, effectively the viewport
//  banner          the widest CMS banner container
//  banner-vertical constrained by height, so it has no width to govern; the
//                  figure is its rendered width at that height
export const VARIANT_RENDER_WIDTHS: Record<ImageVariant, number> = {
  thumbnail: 120,
  card: 320,
  'pdp-main': 600,
  'pdp-zoom': 1000,
  banner: 1050,
  'banner-vertical': 1092
}

// ─── srcset breakpoints ──────────────────────────────────────────────────
// One list per responsive placement. `getSrcSet` takes the list explicitly
// rather than defaulting, so a new call site has to say which slot it is
// filling instead of silently inheriting another placement's widths.

/**
 * Category, listing and featured grids. Spans the 1x mobile tile through the
 * 2x desktop tile that `card` tops out at.
 *
 * Stops at 600 rather than 768: the widest card slot measured anywhere is 309
 * CSS px, so 2x is 618 and 768 would over-serve by a quarter. With 600 as the
 * ceiling the browser lands 3% short at that one extreme and exact everywhere
 * else.
 */
export const CARD_SRCSET_WIDTHS: readonly ApprovedWidth[] = [360, 512, 600]

/** PDP primary image, topping out at the `pdp-main` variant width. */
export const PDP_MAIN_SRCSET_WIDTHS: readonly ApprovedWidth[] = [600, 768, 1024, 1200]

/**
 * `ProductThumbnailCarousel` at `thumbnailSize="large"` — the tablet PDP
 * carousel. It was served a flat 360 before ADO 62980, which under-serves it
 * on every tablet.
 *
 * Tops out at 768 because the widest this carousel ever renders is 40vw of the
 * last viewport before the desktop layout takes over: measured 378 CSS at 959,
 * so 2x is ~756.
 *
 * `.thumbnail-card--large` also carries an `xs-only { width: 100vw }` rule,
 * which looks like it should push this list past 1200. It does not: below the
 * sm breakpoint the PDP renders `ProductImageMobile` instead, and that passes
 * `thumbnailSize="small"`. Verified in the browser at 599 and 600 — the tablet
 * layout is hidden below 600, so that rule never applies to a rendered element.
 */
export const PDP_THUMB_LARGE_SRCSET_WIDTHS: readonly ApprovedWidth[] = [360, 512, 768]

/**
 * `ProductThumbnailGrid` - the desktop lightbox's "view all photos" grid.
 *
 * This slot was invisible to the SC-008 audit until ADO 63439's review: it is
 * the widest `thumbnail` consumer by some way, and unlike every other
 * placement it never caps. Measured tile width: 109 CSS at 960, 128 at 1100,
 * 111 at 1264, 130 at 1440, 180 at 1920 and 243 at 2560, holding at 9.5vw all
 * the way up. The flat 240 variant it used to get is 2x of 120, so the tile was
 * under-served from about 1450px of viewport onward.
 *
 * Stops at 360 rather than reaching 512, the same trade `CARD_SRCSET_WIDTHS`
 * makes: 360 covers 2x up to a 1920 viewport exactly, and the next approved
 * width is a 42% jump that every viewport below 2560 would then pull. A 2x
 * display at 2560 lands 26% short; nothing between 360 and 512 exists to split
 * the difference without adding a width to the governed set.
 */
export const LIGHTBOX_GRID_SRCSET_WIDTHS: readonly ApprovedWidth[] = [240, 300, 360]

/**
 * Every width delivery can be asked for: the variant widths plus every srcset
 * breakpoint, deduplicated and ordered.
 *
 * The missing-image fallback rounds a measured slot up through this list, so a
 * placeholder reuses a transform the CDN has already cached for a real image.
 * Rounding through the breakpoints alone would send a zoom-sized slot straight
 * past `pdp-zoom` to the ceiling, buying a derivative nothing else requests.
 */
export const DELIVERED_WIDTHS: readonly ApprovedWidth[] = [
  ...new Set([
    ...Object.values(IMAGE_VARIANTS)
      .map(variant => variant.width)
      .filter((width): width is ApprovedWidth => width !== undefined),
    ...CARD_SRCSET_WIDTHS,
    ...PDP_MAIN_SRCSET_WIDTHS,
    ...PDP_THUMB_LARGE_SRCSET_WIDTHS,
    ...LIGHTBOX_GRID_SRCSET_WIDTHS
  ])
].sort((a, b) => a - b)

/**
 * The width the missing-image placeholder is delivered at when the slot it
 * stands in for cannot be measured.
 *
 * Not the smallest delivered width: an unmeasurable box is not a small box, and
 * rounding 0 up through `DELIVERED_WIDTHS` would pick the 240px thumbnail for a
 * slot that might be a PDP hero. The stored placeholder is a 2000px master, so
 * it is always resized rather than served whole; 360 matches the loading
 * placeholder and lands around 2 KB.
 */
export const PLACEHOLDER_DEFAULT_WIDTH: ApprovedWidth = 360

/**
 * The widest width delivery asks for: the ceiling the missing-image fallback
 * rounds up to for a slot wider than the last breakpoint, and the cap no
 * variant may exceed.
 */
export const MAX_DELIVERY_WIDTH: ApprovedWidth = 2100

// ─── sizes attributes ────────────────────────────────────────────────────
//
// Measured against this app (ADO 63438), not copied. The nuxt2 -> nuxt4 port
// conserves V2 visuals, so every slot measured here matches the Frontend to
// within a pixel — category tile 172/224/157/214/247 CSS at 414/760/960/1264/
// 1440, PDP main 552, related tile 193, thumb strip 111, featured tile 309.
//
// One figure did NOT carry over: the viewport at which each slot stops growing.
// This repo's containers cap at 1500px CSS by a ~1550px viewport, where the
// Frontend's kept growing to ~1904. Every ladder's px stop therefore sits at
// 1560 here rather than 1904; inheriting 1904 left the `vw` fractions
// over-stating each slot by up to a third across the 1550-1904 band.
// The CSS slot each responsive placement occupies, stated for the browser so
// it can pick a candidate before layout. Each string mirrors a real stylesheet
// rule; the comment names it so the two can be kept in step.

// A ladder stop that is a Vuetify breakpoint, taken from the shared thresholds
// so it moves with the layout it describes. Every breakpoint stop below goes
// through this. A stop written as a pixel literal is a measured width instead:
// 1560 is where the container caps, not a threshold.
const stop = minWidthQuery

// How far below a threshold a `max-width` bound sits, so it cannot overlap the
// `min-width` range that starts at the threshold. The same figure Vuetify's own
// display queries subtract.
const BELOW_THRESHOLD_GAP = 0.02

/**
 * The default category grid: `ProductList` renders `cols="6" sm="4" md="3"`,
 * which is 2 tiles per row below sm, 3 from sm (600px) and 4 from md (960px).
 * There is no change at lg, so this ladder has no 1264 stop.
 *
 * The fractions are not the column fractions. A tile sits inside a Vuetify
 * container with padding and gutters, and that container caps at xl, so a
 * 4-up row is ~17vw of the viewport rather than 25. Measured in the browser:
 * 171 CSS at 414, 224 at 760, 157 at 960, 247 at 1440, 265 at 1920. Each stop
 * below rounds those up a little, so the browser never picks short.
 *
 * `ProductCard` takes this as a prop default; the containers whose grids differ
 * pass their own rather than inheriting a string that does not describe them.
 */
export const CARD_SIZES =
  `(min-width: 1560px) 270px, ${stop('md')} 18vw, ${stop('sm')} 31vw, 43vw`

/**
 * `FeaturedProductList` on the homepage, which chunks by breakpoint rather than
 * by column span: 1 tile per slide below sm, 2 from sm, 4 from md. That makes
 * its tiles much wider than the category grid on small screens and slightly
 * wider on large ones, so it cannot share `CARD_SIZES`.
 *
 * Measured: 243 CSS at 414, 275 at 760, 164 at 960, 284 at 1440, 309 at 1920.
 */
export const FEATURED_CARD_SIZES =
  `(min-width: 1560px) 320px, ${stop('lg')} 21vw, ${stop('md')} 18vw, ${stop('sm')} 38vw, 60vw`

/**
 * `ProductCarousel`'s default grid. `colSize` there is computed from
 * `$vuetify.breakpoint` rather than CSS, so the thresholds below mirror the
 * same ladder: 2 tiles per row at xs, 3 at sm, 4 at md, 6 from lg.
 *
 * Measured in this app: 176 CSS at 414, 227 at 760, 251 at 1100, 219 at 1440,
 * plateauing at 235 from ~1560 once the container caps. As with the category
 * grid these are well under the column fractions, because the container has
 * padding and gutters.
 */
export const CAROUSEL_CARD_SIZES =
  `(min-width: 1560px) 240px, ${stop('lg')} 16vw, ${stop('md')} 24vw, ${stop('sm')} 31vw, 44vw`

/**
 * `ProductRelationshipPanels`, which renders `cols="6" sm="3" md="6" lg="4"`.
 * The column count is not monotonic — it widens again at md — so this cannot be
 * folded into `CARD_SIZES`.
 *
 * This panel sits inside a `v-expansion-panel` that is much narrower than the
 * page container and does not track the viewport evenly: measured 375px wide at
 * a 414 viewport, 721 at 760, 437 at 960, 589 at 1264, 726 at 1920. That is why
 * the sm stop here is *larger* than the md one in vw terms — at 960 the panel
 * collapses to 437px while the grid simultaneously drops from 4-up to 2-up.
 *
 * Tile widths measured through it: 156 CSS at 414, 148 at 760, 186 at 960, 164
 * at 1264, 194 at 1440, 210 at 1920. It is the narrowest card placement in the
 * app, not the widest, so the 600 ceiling in `CARD_SRCSET_WIDTHS` is never a
 * constraint here.
 */
export const RELATED_CARD_SIZES =
  `(min-width: 1560px) 220px, ${stop('lg')} 14vw, ${stop('md')} 20vw, ${stop('sm')} 21vw, 40vw`

/**
 * Recommended products inside the cart notification drawer.
 *
 * Stated in pixels because the drawer is a fixed 360px panel, not a fraction of
 * the viewport — a vw slot would over-state the tile on every screen.
 *
 * Measured at 145 CSS in this app, the same as the Frontend. Deriving it from
 * the panel geometry gives 168 (360 less the `px-3` padding either side, split
 * two up by `cols="6"`), which misses ProductCard's own `px-2` and the image
 * border. Both land on the same candidate at 1x and 2x, but the figure here is
 * the measured one.
 */
export const CART_DRAWER_CARD_SIZES = '150px'

/**
 * `ProductMediaContainer`'s slot: full width on mobile and tablet, and a
 * proportional column on desktop that caps once the container does.
 *
 * Measured: 399 CSS at 414 (~96vw), 352 at 960, 478 at 1264, 552 at 1440, 593
 * at 1920. A flat `593px` across the whole desktop range — which this was —
 * asks for 68% more pixels than the slot renders at 960.
 */
export const PDP_MAIN_SIZES =
  `(min-width: 1560px) 593px, ${stop('md')} 39vw, 100vw`

/**
 * Where the PDP image preloads apply: the mobile and desktop layouts, which
 * show both the main image (`ProductMediaContainer` on mobile,
 * `ProductImageCarousel` on desktop) and the `thumbnail` strip. The tablet
 * layout (Vuetify `sm`) shows only the large thumbnail carousel, so both
 * preloads were fetched there and never used (ADO 63948, 63982). Built from
 * the shared thresholds so it moves with the layout switch in
 * `ProductImage.vue`; Vuetify reads `window.innerWidth`, which is the width a
 * media query tests.
 */
export const PDP_PRELOAD_MEDIA =
  `(max-width: ${breakpoints.sm - BELOW_THRESHOLD_GAP}px), ${stop('md')}`

/**
 * The tablet band, where `ProductImage.vue` shows `ProductImageTablet` - the
 * complement of `PDP_PRELOAD_MEDIA`.
 *
 * Until it has measured the viewport, `ProductImage.vue` renders the family it
 * guesses from the User-Agent. Whenever that guess is desktop - a tablet with a
 * desktop UA, such as iPadOS Safari - the desktop layout reaches this band, and
 * a hidden `<img>` is still fetched. The desktop strip and main image blank
 * themselves over this band (`SImage`'s `blankMedia`), so a tablet requests none
 * of the images it is about to swap out (ADO 63982). Mobile is left alone:
 * `ProductImageMobile` shows the same URLs, so an early fetch is a cache hit there.
 */
export const PDP_TABLET_MEDIA =
  `${stop('sm')} and (max-width: ${breakpoints.md - BELOW_THRESHOLD_GAP}px)`

/**
 * The header's two logos: the full one from lg (`HeaderLogo`, hidden-md-and-down)
 * and the typeset one below it (`HeaderLayout`, hidden-lg-and-up). A hidden `<img>`
 * is still fetched, early in every page load, so each blanks itself over the band
 * where it is hidden (`SBlankablePicture`).
 */
export const HEADER_LOGO_BLANK_MEDIA = `(max-width: ${breakpoints.lg - BELOW_THRESHOLD_GAP}px)`
export const TYPESET_LOGO_BLANK_MEDIA = stop('lg')

/**
 * `ProductThumbnailGrid`'s tile, measured above.
 *
 * Two stops because the column count is not monotonic: the grid drops from
 * `repeat(4, 1fr)` to `repeat(3, 1fr)` at 1200px, which makes each tile *wider*
 * - 111 CSS at 1264 against 128 at 1100. Both fractions round the measurements
 * up slightly so the browser never picks short.
 */
export const LIGHTBOX_GRID_SIZES = `${stop('lg')} 9.6vw, 12vw`

/**
 * Mirrors `.thumbnail-card--large` in `ProductThumbnailCarousel`.
 *
 * The stylesheet's `xs-only` 100vw rule is deliberately not mirrored here: the
 * carousel is never rendered below the sm breakpoint (see
 * `PDP_THUMB_LARGE_SRCSET_WIDTHS`), so a `(max-width: 599px)` stop would be a
 * branch the browser can never take.
 */
export const PDP_THUMB_LARGE_SIZES = '40vw'
